import { apiFetch, httpError } from '../services/apiFetch';

const API_URL = import.meta.env.VITE_API_URL;

// Multipart upload. We intentionally do NOT set Content-Type — the browser
// adds `multipart/form-data; boundary=...` itself. apiFetch detects the
// FormData body and skips its default JSON content-type for us.
export async function uploadReceipt(expenseId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiFetch(`${API_URL}/api/Receipt/UploadReceipt/${expenseId}`, {
        method: 'POST',
        body: formData,
    });
    if (!res.ok) throw await httpError(res, 'Failed to upload receipt');
    return res.json();
}

export async function fetchReceiptsByExpense(expenseId) {
    const res = await apiFetch(`${API_URL}/api/Receipt/GetReceiptsByExpense/${expenseId}`, { method: 'GET' });
    if (!res.ok) throw await httpError(res, 'Failed to fetch receipts');
    return res.json();
}

// `inline=true` returns a SAS URL with Content-Disposition: inline so the
// browser renders it in an <iframe>/<img> instead of triggering the download
// prompt. Used by the in-modal preview; the default (false) backs explicit
// Download buttons.
export async function fetchReceiptDownloadUrl(id, { inline = false } = {}) {
    const qs = inline ? '?inline=true' : '';
    const res = await apiFetch(`${API_URL}/api/Receipt/GetDownloadUrl/${id}${qs}`, { method: 'GET' });
    if (!res.ok) throw await httpError(res, 'Failed to prepare download');
    return res.json();
}

export async function deleteReceipt(id) {
    const res = await apiFetch(`${API_URL}/api/Receipt/DeleteReceipt/${id}`, { method: 'POST' });
    if (!res.ok) throw await httpError(res, 'Failed to delete receipt');
}

// Async scan pipeline entry point. Uploads to a pending blob and queues a
// ReceiptScanJob server-side; the worker handles tier 1/2/3 OCR and commits
// an Expense + Receipt when ready, surfacing progress via the UpdateScanJobs
// SignalR fire. Returns the new ScanJobViewModel so the modal can show the
// row before the first SignalR fire arrives.
export async function queueReceiptScan(jobId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiFetch(`${API_URL}/api/Receipt/QueueReceiptScan?jobId=${jobId}`, {
        method: 'POST',
        body: formData,
    });
    if (!res.ok) throw await httpError(res, 'Failed to queue receipt scan');
    return res.json();
}

// Source-of-truth for the banner. Returns Queued/Processing rows + the last
// 30 days of Failed rows scoped to this job. Surviving Completed rows live
// in the expense table, not the banner — the server filters them out.
export async function fetchActiveScanJobs(jobId) {
    const res = await apiFetch(`${API_URL}/api/Receipt/GetActiveScanJobs/${jobId}`, { method: 'GET' });
    if (!res.ok) throw await httpError(res, 'Failed to fetch active scan jobs');
    return res.json();
}

// Failed → Queued bounce. The server re-uses the existing pending blob
// (kept on Failed precisely so retry is free) and writes the id back to
// the worker's channel.
export async function retryScanJob(id) {
    const res = await apiFetch(`${API_URL}/api/Receipt/RetryScanJob/${id}`, { method: 'POST' });
    if (!res.ok) throw await httpError(res, 'Failed to retry scan job');
}

// Drop a scan job entirely. Deletes the pending blob (when still pre-commit)
// and removes the row. Used by the banner's Discard button and as the
// cleanup step after a successful Fill manually commit.
export async function discardScanJob(id) {
    const res = await apiFetch(`${API_URL}/api/Receipt/DiscardScanJob/${id}`, { method: 'POST' });
    if (!res.ok) throw await httpError(res, 'Failed to discard scan job');
}
